import React, { useState } from 'react';
// Fix: Use namespace import to bypass missing exported member errors in react-router-dom
import * as ReactRouterDom from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Calendar, UserCheck, Edit3, Trash2, 
  Building2, StickyNote, Hash, Upload, AlertCircle, Tag, Copy, MapPin, Users,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { formatDateSafe, formatFullDateSafe } from '../lib/utils';
import { SessionFormModal } from '../features/programs/SessionFormModal';
import { AttendanceModule } from '../features/programs/attendance/AttendanceModule';
import { SessionVolunteerManager } from '../features/programs/SessionVolunteerManager';

const { useParams, useNavigate, Link } = ReactRouterDom as any;

const getSessionStatusStyles = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'upcoming': return 'bg-blue-50 text-blue-600 border-blue-100';
    case 'delivered': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    case 'cancelled': return 'bg-rose-50 text-rose-600 border-rose-100';
    default: return 'bg-slate-50 text-slate-400 border-slate-100';
  }
};

const SessionDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);

  const [activeTab, setActiveTab] = useState<'attendance' | 'volunteering'>('attendance');

  const { data: sessionData, isLoading, error: queryError } = useQuery({
    queryKey: ['session', id],
    queryFn: async () => {
      const { data: session, error: sErr } = await supabase
        .from('sessions')
        .select(`
          *, 
          engagements(*, programs(*), participation(*, people(*)), regions:region_id(label)),
          host_org:organizations(id, name, street_address, city, state, zip_code),
          session_durations(label),
          session_service_types(label),
          session_delivery_modes(label),
          session_staff(
            person_id, 
            role_id, 
            people:person_id(first_name, last_name, email),
            session_staff_roles:role_id(label)
          )
        `)
        .eq('id', id)
        .single();
      
      if (sErr) throw sErr;
      
      const headcount = session.headcount_count || 0;

      const { data: attendance, error: aErr } = await supabase
        .from('attendance')
        .select('*, people:person_id(first_name, last_name, email)')
        .eq('session_id', id);
      
      if (aErr) throw aErr;

      return { session, attendance, headcount };
    }
  });

  const deleteSession = useMutation({
    mutationFn: async () => { await supabase.from('sessions').delete().eq('id', id); },
    onSuccess: () => { navigate(`/engagements/${sessionData?.session?.engagement_id}`); }
  });

  if (isLoading) return (
    <div className="p-20 text-center space-y-4">
      <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto" />
      <p className="uppercase font-black text-slate-300 tracking-widest text-[10px]">Loading operational window...</p>
    </div>
  );

  if (queryError || !sessionData?.session) return (
    <div className="p-20 text-center max-w-md mx-auto space-y-6">
      <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center mx-auto shadow-inner">
        <AlertCircle size={32} />
      </div>
      <div className="space-y-2">
        <h3 className="text-xl font-black text-slate-900 uppercase">Registry Error</h3>
        <p className="text-xs font-bold text-slate-400 uppercase leading-relaxed tracking-wider">
          Failed to resolve session context. {queryError?.message || 'The resource may have been relocated.'}
        </p>
      </div>
      <Button variant="outline" onClick={() => navigate(-1)}>Return to Safety</Button>
    </div>
  );
  
  const session = sessionData.session;
  const staff = session?.session_staff || [];
  const roster = session?.engagements?.participation || [];
  const attendance = sessionData?.attendance || [];
  const headcount = sessionData?.headcount || 0;
  const parentProgram = session?.engagements?.programs;
  const trackingMode = session?.engagements?.tracking_mode || 'roster';

  const canImport = profile?.role === 'system_admin' || profile?.role === 'org_manager';

  return (
    <div className="space-y-8 pb-20 max-w-7xl mx-auto animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(`/engagements/${session.engagement_id}`)} className="p-2 hover:bg-white rounded-md border border-transparent hover:border-slate-200 transition-all text-slate-400">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2 mb-1 leading-none">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-900 cursor-pointer transition-colors" onClick={() => navigate(`/programs/${parentProgram?.id}`)}>{parentProgram?.name}</span>
              <span className="text-slate-200 font-light">/</span>
              <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest hover:text-blue-800 cursor-pointer transition-colors" onClick={() => navigate(`/engagements/${session.engagement_id}`)}>{session.engagements?.name}</span>
            </div>
            <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight leading-none mb-2">
              {session.name || 'Operational Window'}
            </h1>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-slate-900 text-[#FFDB00] text-[8px] font-black uppercase tracking-tighter border border-slate-700 shadow-sm">
                 <div className="w-1 h-1 rounded-full bg-[#FFDB00] animate-pulse" />
                 SESSION
              </span>
              <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase border tracking-widest ${getSessionStatusStyles(session.status || 'upcoming')}`}>
                {session.status || 'upcoming'}
              </span>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest border border-slate-100 px-2 py-0.5 rounded bg-white">
                {formatFullDateSafe(session.session_datetime)}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
           {trackingMode === 'drop_in' && canImport && (
             <Link to={`/import?engagementId=${session.engagement_id}&sessionId=${id}`}>
               <Button variant="outline" className="text-blue-600 border-blue-100 hover:bg-blue-50" icon={<Upload size={14} />}>Import Attendance</Button>
             </Link>
           )}
           <Button variant="outline" onClick={() => setIsCloneModalOpen(true)} icon={<Copy size={14} />}>Clone</Button>
           <Button variant="outline" onClick={() => setIsEditModalOpen(true)} icon={<Edit3 size={14} />}>Modify</Button>
           <button onClick={() => { if(confirm('Delete operational window?')) deleteSession.mutate(); }} className="p-2.5 text-rose-500 bg-white border border-slate-200 rounded-md hover:bg-rose-50 transition-all shadow-sm">
             <Trash2 size={16} />
           </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <div className="p-8 border-b border-slate-100 flex flex-wrap items-center gap-10 bg-white">
          <div className="flex items-center gap-3">
             <div className="w-8 h-8 bg-slate-900 text-[#FFDB00] rounded-md flex items-center justify-center shadow-md"><Calendar size={16} /></div>
             <div>
               <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">operational date</p>
               <p className="text-sm font-bold text-slate-900 uppercase">{formatDateSafe(session.session_datetime)}</p>
             </div>
          </div>
          <div className="flex items-center gap-3">
             <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-md flex items-center justify-center"><Hash size={16} /></div>
             <div>
               <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">duration</p>
               <p className="text-sm font-bold text-slate-900 uppercase">{session.session_durations?.label || 'Not Specified'}</p>
             </div>
          </div>
          <div className="flex items-center gap-3">
             <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-md flex items-center justify-center"><UserCheck size={16} /></div>
             <div>
               <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Service Type</p>
               <p className="text-sm font-bold text-slate-900 uppercase">{session.session_service_types?.label || 'Not Specified'}</p>
             </div>
          </div>
          <div className="flex items-center gap-3">
             <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-md flex items-center justify-center"><Building2 size={16} /></div>
             <div>
               <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Delivery Mode</p>
               <p className="text-sm font-bold text-slate-900 uppercase">{session.session_delivery_modes?.label || 'Not Specified'}</p>
             </div>
          </div>
          {session.engagements?.regions?.label && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-amber-50 text-amber-600 rounded-md flex items-center justify-center border border-amber-100 shadow-sm"><MapPin size={16} /></div>
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Region</p>
                <p className="text-sm font-bold text-slate-900 uppercase">{session.engagements.regions.label}</p>
              </div>
            </div>
          )}
          {session.session_hours > 0 && (
            <div className="flex items-center gap-3 animate-in fade-in slide-in-from-left-2">
              <div className={`w-8 h-8 rounded-md flex items-center justify-center border shadow-sm ${
                session.session_service_types?.label?.toLowerCase().includes('instruction') 
                  ? 'bg-blue-50 text-blue-600 border-blue-100' 
                  : 'bg-teal-50 text-teal-600 border-teal-100'
              }`}><Clock size={16} /></div>
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                  {session.session_service_types?.label?.toLowerCase().includes('instruction') ? 'instructional hours' : 'coaching hours'}
                </p>
                <p className="text-sm font-bold text-slate-900 uppercase">{session.session_hours} hours</p>
              </div>
            </div>
          )}
          {trackingMode === 'headcount' && (
            <div className="flex items-center gap-3 animate-in fade-in slide-in-from-left-2">
              <div className="w-8 h-8 bg-emerald-50 text-emerald-600 rounded-md flex items-center justify-center border border-emerald-100 shadow-sm"><Hash size={16} /></div>
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">headcount</p>
                <p className="text-sm font-bold text-slate-900 uppercase">{headcount} served</p>
              </div>
            </div>
          )}
          {session.host_org && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-50 text-purple-600 rounded-md flex items-center justify-center border border-purple-100 shadow-sm"><Building2 size={16} /></div>
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">host</p>
                <p className="text-sm font-bold text-slate-900 uppercase truncate max-w-[200px] text-purple-900">{session.host_org.name}</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 border-b border-slate-100">
          <div className="p-8 space-y-4">
             <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><UserCheck size={14} className="text-emerald-500" /> Assigned Staffing</h3>
             <div className="flex flex-wrap gap-3">
               {staff.length === 0 ? (
                 <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest italic p-4 border border-dashed border-slate-200 rounded w-full text-center">Unstaffed window</p>
               ) : (
                 staff.map((s: any) => (
                   <div key={s.person_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-md border border-slate-100 shadow-sm">
                     <div className="w-8 h-8 bg-slate-900 text-[#FFDB00] rounded-md flex items-center justify-center font-black text-[9px] shadow-sm">{s.people?.first_name?.[0] || '?'}{s.people?.last_name?.[0] || '?'}</div>
                     <div>
                       <p className="text-11px font-bold text-slate-900 uppercase leading-none">{s.people?.first_name} {s.people?.last_name}</p>
                       <p className="text-[8px] font-bold text-blue-600 uppercase tracking-widest mt-0.5">{s.session_staff_roles?.label}</p>
                     </div>
                   </div>
                 ))
               )}
             </div>
          </div>
          <div className="p-8 bg-slate-50/20 border-l border-slate-100 space-y-4">
             <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><StickyNote size={14} className="text-blue-500" /> notes</h3>
             <div className="text-xs font-medium text-slate-600 leading-relaxed bg-white p-6 rounded-md border border-slate-100 min-h-[100px] whitespace-pre-wrap tracking-wide shadow-inner">
               {session.notes || 'No administrative logistics recorded.'}
             </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex items-center gap-8 border-b border-slate-200 px-1">
          <button 
            onClick={() => setActiveTab('attendance')}
            className={`text-[10px] font-black uppercase tracking-widest flex items-center gap-2 pb-3 transition-all border-b-2 ${activeTab === 'attendance' ? 'text-slate-900 border-slate-900' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
          >
            <UserCheck size={14} /> Attendance Manifest
          </button>
          <button 
            onClick={() => setActiveTab('volunteering')}
            className={`text-[10px] font-black uppercase tracking-widest flex items-center gap-2 pb-3 transition-all border-b-2 ${activeTab === 'volunteering' ? 'text-slate-900 border-slate-900' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
          >
            <Users size={14} /> Volunteering Impact Log
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-8 min-h-[400px]">
          {activeTab === 'attendance' ? (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <AttendanceModule 
                sessionId={id!}
                engagementId={session.engagement_id}
                trackingMode={trackingMode}
                roster={roster}
                attendance={attendance}
                headcount={headcount}
              />
            </div>
          ) : (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <SessionVolunteerManager sessionId={id!} />
            </div>
          )}
        </div>
      </div>
      
      {isEditModalOpen && <SessionFormModal engagementId={session.engagement_id} session={session} onClose={() => setIsEditModalOpen(false)} />}
      {isCloneModalOpen && <SessionFormModal engagementId={session.engagement_id} session={session} isClone={true} onClose={() => setIsCloneModalOpen(false)} />}
    </div>
  );
};

export default SessionDetail;